import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getProductName } from 'src/components-bl/MerchandisingRules/merchandisingRules.helpers';
import { getViewConfigAdapter } from './viewConfigAdapters';
import { ViewConfigEditorProps, ViewConfigValue, UseViewConfigEditorReturnType } from './types';

export function useViewConfigEditor({
  shopId,
  surface,
  controlVariantId,
  testVariantId,
  onChangeCountChange,
  onSaveReady,
}: ViewConfigEditorProps): UseViewConfigEditorReturnType {
  const adapter = getViewConfigAdapter(surface);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [control, setControl] = useState<ViewConfigValue | undefined>();
  const [test, setTest] = useState<ViewConfigValue | undefined>();

  const loadValues = useCallback((): Promise<void> => {
    if (!adapter || !controlVariantId || !testVariantId) {
      setIsLoading(false);
      return Promise.resolve();
    }

    setIsLoading(true);
    setHasError(false);
    return Promise.all([
      adapter.get({ shopId, variantId: controlVariantId }),
      adapter.get({ shopId, variantId: testVariantId }),
    ])
      .then(([controlValue, testValue]) => {
        setControl(controlValue);
        setTest(testValue);
      })
      .catch(() => {
        setControl(undefined);
        setTest(undefined);
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [adapter, shopId, controlVariantId, testVariantId]);

  useEffect(() => {
    loadValues();
  }, [loadValues]);

  useEffect(() => {
    if (!control || !test) {
      onChangeCountChange?.(0);
      return;
    }

    const changeCount =
      (control.numberOfResultsToDisplay !== test.numberOfResultsToDisplay ? 1 : 0) +
      (control.addToCartActive !== test.addToCartActive ? 1 : 0);

    onChangeCountChange?.(changeCount);
  }, [control, test, onChangeCountChange]);

  // Fast Continue click right after a toggle could otherwise race the in-flight update -
  // `pendingOperationRef` lets `save()` wait for it instead of navigating away before it lands.
  const pendingOperationRef = useRef<Promise<void>>(Promise.resolve());

  // Update local state optimistically instead of re-running `loadValues()` (which flips
  // `isLoading` back to true and made `ViewConfigEditor` swap its whole tree out for a "Loading..."
  // placeholder on every single click) - only re-fetch from the server if the write itself fails,
  // to resync with whatever's actually persisted.
  const queueUpdate = (value: Partial<ViewConfigValue>): Promise<void> => {
    const operation = pendingOperationRef.current
      .catch(() => undefined)
      .then(() => {
        if (!adapter || !testVariantId) return undefined;
        return adapter.update({ shopId, variantId: testVariantId, value }).catch(() => {
          setHasError(true);
          return loadValues();
        });
      });
    pendingOperationRef.current = operation;
    return operation;
  };

  const setResultsCount = async (value: number): Promise<void> => {
    if (!adapter || !testVariantId) return;
    setTest(prev => (prev ? { ...prev, numberOfResultsToDisplay: value } : prev));
    await queueUpdate({ numberOfResultsToDisplay: value });
  };

  const setAddToCartActive = async (active: boolean): Promise<void> => {
    if (!adapter || !testVariantId) return;
    setTest(prev => (prev ? { ...prev, addToCartActive: active } : prev));
    await queueUpdate({ addToCartActive: active });
  };

  const save = useCallback(
    (): Promise<boolean> => pendingOperationRef.current.then(() => true).catch(() => false),
    []
  );

  useEffect(() => {
    onSaveReady?.(save);
  }, [save, onSaveReady]);

  const controlItems = useMemo(
    () =>
      control
        ? [
            { label: 'Results shown', value: String(control.numberOfResultsToDisplay) },
            { label: 'Add to cart on carousel', value: control.addToCartActive ? 'On' : 'Off' },
          ]
        : [],
    [control]
  );

  const changeCount =
    control && test
      ? (control.numberOfResultsToDisplay !== test.numberOfResultsToDisplay ? 1 : 0) +
        (control.addToCartActive !== test.addToCartActive ? 1 : 0)
      : 0;

  return {
    isLoading,
    hasError,
    isSupported: Boolean(adapter),
    resultsCountOptions: adapter?.resultsCountOptions ?? [],
    control,
    test,
    controlItems,
    changeCount,
    surfaceLabel: getProductName(surface),
    setResultsCount,
    setAddToCartActive,
  };
}
